import { TaskPriority } from "@jugl-web/rest-api/tasks";
import { useTranslations } from "@jugl-web/utils";
import { ReactNode, useCallback, useMemo } from "react";
import { ReactComponent as PriorityBlankIcon } from "./assets/priority-blank.svg";

export interface TaskPriorityDetails {
  id: TaskPriority;
  shortLabel: string;
  longLabel: string;
  textColor: string;
  color: string;
  icon: ReactNode;
}

export const useTaskPriorities = () => {
  const { t } = useTranslations();

  const taskPriorityDetailsById = useMemo<
    Record<TaskPriority, TaskPriorityDetails>
  >(
    () => ({
      [TaskPriority.none]: {
        id: TaskPriority.none,
        longLabel: t({
          id: "tasks-page.none-priority-long-label",
          defaultMessage: "Not Assigned",
        }),
        shortLabel: t({
          id: "tasks-page.none-priority-short-label",
          defaultMessage: "None",
        }),
        textColor: "#737B7E",
        color: "#BDBDBD",
        icon: <PriorityBlankIcon className="shrink-0 text-primary" />,
      },
      [TaskPriority.low]: {
        id: TaskPriority.low,
        longLabel: t({
          id: "tasks-page.low-priority-long-label",
          defaultMessage: "Low priority",
        }),
        shortLabel: t({
          id: "tasks-page.low-priority-short-label",
          defaultMessage: "Low",
        }),
        textColor: "#398453",
        color: "#50BA4D",
        icon: <PriorityBlankIcon className="shrink-0 text-secondary" />,
      },
      [TaskPriority.medium]: {
        id: TaskPriority.medium,
        longLabel: t({
          id: "tasks-page.medium-priority-long-label",
          defaultMessage: "Medium priority",
        }),
        shortLabel: t({
          id: "tasks-page.medium-priority-short-label",
          defaultMessage: "Medium",
        }),
        textColor: "#B15806",
        color: "#FF8F27",
        icon: <PriorityBlankIcon className="shrink-0 text-[#FF8F27]" />,
      },
      [TaskPriority.high]: {
        id: TaskPriority.high,
        longLabel: t({
          id: "tasks-page.high-priority-long-label",
          defaultMessage: "High priority",
        }),
        shortLabel: t({
          id: "tasks-page.high-priority-short-label",
          defaultMessage: "High",
        }),
        textColor: "#C94841",
        color: "#EA5353",
        icon: <PriorityBlankIcon className="shrink-0 text-gradients-g6" />,
      },
    }),
    [t]
  );

  const getPriorityDetailsById = useCallback(
    (priority: TaskPriority): TaskPriorityDetails =>
      taskPriorityDetailsById[priority],
    [taskPriorityDetailsById]
  );

  const allPriorities = useMemo<TaskPriorityDetails[]>(() => {
    const orderedPriorities = [
      TaskPriority.none,
      TaskPriority.low,
      TaskPriority.medium,
      TaskPriority.high,
    ];

    return orderedPriorities.map(getPriorityDetailsById);
  }, [getPriorityDetailsById]);

  return {
    allPriorities,
    getPriorityDetailsById,
  };
};
